import { useEffect, useState } from 'react';

interface Toast {
  id: string;
  message: string;
  type: 'info' | 'success' | 'error' | 'warning';
  isExiting?: boolean;
}

const DISPLAY_MS = 3700;
const EXIT_MS = 280;

const toasts: Toast[] = [];
let listeners: (() => void)[] = [];

function notify() {
  listeners.forEach((l) => l());
}

// eslint-disable-next-line react-refresh/only-export-components
export function showToast(message: string, type: Toast['type'] = 'info') {
  const toast: Toast = { id: Math.random().toString(36).slice(2), message, type };
  toasts.push(toast);
  notify();

  // Begin exit animation before removing
  setTimeout(() => {
    const idx = toasts.findIndex((t) => t.id === toast.id);
    if (idx >= 0) {
      toasts[idx] = { ...toasts[idx], isExiting: true };
      notify();
      setTimeout(() => {
        const idx2 = toasts.findIndex((t) => t.id === toast.id);
        if (idx2 >= 0) {
          toasts.splice(idx2, 1);
          notify();
        }
      }, EXIT_MS);
    }
  }, DISPLAY_MS);
}

const bgColors: Record<Toast['type'], string> = {
  info: 'bg-bss-blue',
  success: 'bg-emerald-600',
  error: 'bg-red-600',
  warning: 'bg-amber-500',
};

const icons: Record<Toast['type'], string> = {
  info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  success: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  error: 'M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  warning: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z',
};

export function Toasts() {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const listener = () => forceUpdate((n) => n + 1);
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  }, []);

  return (
    <div className="pointer-events-none fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 flex-col-reverse items-center gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`${bgColors[toast.type]} pointer-events-auto flex items-center gap-2 rounded-full pl-3 pr-4 py-2.5 text-sm font-medium text-white shadow-lg ${
            toast.isExiting ? 'animate-toast-out' : 'animate-toast-in'
          }`}
        >
          <svg className="h-4 w-4 shrink-0 opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d={icons[toast.type]} />
          </svg>
          {toast.message}
        </div>
      ))}
    </div>
  );
}
